import { useCallback, useEffect, useId, useRef, useState } from 'react'

/**
 * Open, closed, and the three ways a top-bar menu gets out of the way.
 *
 * Lifted out of `AccountMenu` when the help menu became the second popover in the top bar, because
 * two hand-copied sets of dismissal rules are two sets that drift - and each rule was written for a
 * way a menu becomes annoying to use. Escape closes it. A pointer going down anywhere outside closes
 * it. And the toggle is excluded from that, or it could never close the menu: the outside handler
 * would close it on pointerdown and the button's own click would immediately reopen it.
 *
 * Non-modal on purpose, which is the difference from `Dialog`. A menu does not trap focus or make the
 * page inert; it is a list of things to do next, and the page behind it is still the page.
 */
export function usePopover() {
  const [open, setOpen] = useState(false)
  const container = useRef<HTMLDivElement>(null)
  const toggle = useRef<HTMLButtonElement>(null)
  const listId = useId()

  const close = useCallback(() => setOpen(false), [])

  useEffect(() => {
    if (!open) return

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') close()
    }

    function onPointerDown(event: PointerEvent) {
      const target = event.target as Node
      if (container.current?.contains(target)) return
      if (toggle.current?.contains(target)) return
      close()
    }

    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('pointerdown', onPointerDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('pointerdown', onPointerDown)
    }
  }, [open, close])

  return { open, setOpen, close, container, toggle, listId }
}
